/** @format */
import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { _$, Form, FormItem, Radio, Select, Option, Message, Drawer } from '@king-design/react'
import SelectedInstance from './SelectedInstance'
import ConditionMatch, { ConditionMatchHandle } from './ConditionMatch'
import { ProductItem } from 'src/utils/interface'
import { set, cloneDeep } from 'lodash'
import { handleParamsCase, request } from 'src/utils'
import ApiGroup from 'src/api/ApiGroup'
import { GroupProductProps } from './constants'

interface EditGroupDrawerProps {
  className?: string
  groupId: string
  visible: boolean
  drawerType: 'edit' | 'create'
  defaultData: GroupProductProps
  productList: ProductItem[]
  onClose: (updata: boolean, productType?: number) => void
}

const EditGroupDrawer: React.FC<EditGroupDrawerProps> = ({
  className,
  groupId,
  visible,
  drawerType,
  defaultData,
  productList,
  onClose
}) => {
  const [formValue, setFormValue] = useState<GroupProductProps>({} as GroupProductProps)
  const formRef = useRef<any>()
  const conditonMatchRef = useRef<ConditionMatchHandle>()
  const [addInstances, setInstances] = useState<string[]>([])

  useEffect(() => {
    if (visible) {
      defaultData
        ? setFormValue({ ...defaultData, resourceBindType: defaultData.groupDynamicRules ? 2 : 1 })
        : setFormValue({ resourceBindType: 1 } as GroupProductProps)
    } else {
      setFormValue(undefined)
    }
  }, [visible])

  // 提交
  const drawerOk = async () => {
    const validate = await formRef.current?.validate()
    if (validate || drawerType === 'edit') {
      const params = {
        GroupId: parseInt(groupId)
      }
      if (formValue.resourceBindType === 2) {
        const filter = await conditonMatchRef?.current?.getValues()
        if (filter) {
          set(params, 'GroupDynamicRules', [
            {
              ProductType: formValue.productType,
              FilterRelation: filter.filterRelation,
              Filters: handleParamsCase(cloneDeep(filter.filters))
            }
          ])
          const result = await request(ApiGroup.setGroupProductRules, {
            method: 'POST',
            data: params
          })
          if (result.code === 'Success') {
            onClose(true, formValue.productType)
            Message.success(drawerType === 'create' ? _$('新建成功') : _$('编辑成功'))
          }
        } else {
          return
        }
      } else if (formValue.resourceBindType === 1) {
        const instances = addInstances.map(item => ({
          ProductType: formValue.productType,
          InstanceId: item
        }))
        set(params, 'Instances', instances)
        const result = await request(ApiGroup.addGroupProductInstance, {
          method: 'POST',
          data: params
        })
        if (result.code === 'Success') {
          onClose(true, formValue.productType)
          Message.success(drawerType === 'create' ? _$('新建成功') : _$('编辑成功'))
        }
      } else {
        return
      }
    }
  }

  return (
    <Drawer
      value={visible}
      mode="destroy"
      ok={() => {
        drawerOk()
      }}
      onChangeValue={show => {
        if (!show) {
          onClose(false)
        }
      }}
      disabledOk={formValue?.resourceBindType === 1 && !addInstances?.length}
      title={drawerType === 'create' ? _$('新建资源') : defaultData?.productName}
    >
      <div className={className}>
        {drawerType === 'create' ? (
          <Form starOnRequired ref={formRef}>
            <div className="create-card">
              <FormItem
                label={_$('产品类型')}
                value={formValue?.productType}
                rules={{ required: true }}
                messages={{
                  required: _$('请选择产品类型')
                }}
              >
                <Select
                  value={formValue?.productType}
                  filterable
                  disabled={defaultData?.productType !== undefined}
                  onChangeValue={value => {
                    const find = productList.find(product => product.productType === value)
                    const { childTypes = [] } = find || {}
                    setFormValue({
                      ...formValue,
                      productType: value,
                      childTypes
                    })
                  }}
                >
                  {productList?.length && defaultData?.productType === undefined
                    ? productList.map(({ productType, productName }) => (
                        <Option key={productType} value={productType}>
                          {_$(productName)}
                        </Option>
                      ))
                    : null}
                  {defaultData?.productType !== undefined ? (
                    <Option key={defaultData.productType} value={defaultData.productType}>
                      {_$(defaultData.productName)}
                    </Option>
                  ) : null}
                </Select>
              </FormItem>
              <FormItem
                label={_$('新建实例')}
                value={formValue?.resourceBindType}
                rules={{ required: true }}
              >
                <div className="radio-group">
                  <Radio
                    name="resourceBindType"
                    disabled={defaultData?.productType !== undefined}
                    trueValue={1}
                    value={formValue?.resourceBindType}
                    onChangeValue={(value: any) => {
                      setFormValue({
                        ...formValue,
                        resourceBindType: value
                      })
                    }}
                  >
                    {_$('自选实例')}
                  </Radio>
                  {formValue?.productType === 0 ? (
                    <Radio
                      name="resourceBindType"
                      disabled={defaultData?.productType !== undefined}
                      trueValue={2}
                      value={formValue?.resourceBindType}
                      onChangeValue={(value: any) => {
                        setFormValue({
                          ...formValue,
                          resourceBindType: value
                        })
                      }}
                    >
                      {_$('条件匹配')}
                    </Radio>
                  ) : null}
                </div>
              </FormItem>
            </div>
          </Form>
        ) : null}
        <div>
          {formValue?.productType !== undefined && formValue?.resourceBindType === 1 ? (
            <SelectedInstance
              groupId={groupId}
              addInstances={addInstances}
              setInstances={setInstances}
              productType={formValue?.productType}
              childTypes={formValue?.childTypes}
            />
          ) : null}
          {formValue?.productType !== undefined && formValue?.resourceBindType === 2 ? (
            <ConditionMatch
              ref={conditonMatchRef}
              filterRelation={formValue?.groupDynamicRules?.relation}
              filters={formValue?.groupDynamicRules?.filters}
            />
          ) : null}
        </div>
      </div>
    </Drawer>
  )
}

export default styled(EditGroupDrawer)`
  .create-card {
    padding-bottom: 16px;
    border-bottom: 1px solid #eee;
    margin-bottom: 16px;
  }
  .k-label {
    width: auto;
  }
  .radio-group {
    .k-radio {
      margin-right: 8px;
    }
  }
`
